import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiClient } from 'services'
import posthog from 'posthog-js';

const AuthContext = createContext({});

export function isValidCurrentUserResponse(data) {
  return Boolean(
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    data.user &&
    typeof data.user === 'object'
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Token management
  const getStoredTokens = () => {
    const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
    return {
      accessToken: storage.getItem('accessToken'),
      refreshToken: storage.getItem('refreshToken')
    };
  };

  const storeTokens = (accessToken, refreshToken, rememberMe = false) => {
    const storage = rememberMe ? localStorage : sessionStorage;

    // Clear from both storages first
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');

    // Store in the appropriate storage
    storage.setItem('accessToken', accessToken);
    storage.setItem('refreshToken', refreshToken);

    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }
  };

  const clearTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
  };

  // Fetch current user
  const fetchCurrentUser = useCallback(async () => {
    try {
      const { accessToken } = getStoredTokens();
      if (!accessToken) {
        setLoading(false);
        return;
      }

      // Set the token in axios headers
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      const response = await apiClient.get('/api/auth/me');
      if (!isValidCurrentUserResponse(response?.data)) {
        throw new Error('Invalid auth response shape');
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      if (response.data.user?.id) {
        posthog.identify(response.data.user.id, {
          email: response.data.user.email,
          name: response.data.user.name
        });
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      // If both tokens are expired, the interceptor will handle logout
      // Just clear state here to avoid inconsistency
      clearTokens();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await apiClient.post('/api/auth/login', { email, password });
      const { access_token, refresh_token, user } = response.data;

      // Store tokens
      storeTokens(access_token, refresh_token, rememberMe);

      // Set axios default header
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Update state
      setUser(user);
      setIsAuthenticated(true);
      if (user?.id) {
        posthog.identify(user.id, {
          email: user.email,
          name: user.name
        });
      }

      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint (optional, since JWT is stateless)
      // Use skipAuthRefresh to prevent token refresh attempts during logout
      await apiClient.post('/api/auth/logout', {}, { skipAuthRefresh: true });
    } catch (error) {
      // Ignore errors during logout (e.g., expired tokens)
      // The important part is clearing local state
      console.log('Logout endpoint call failed (ignored):', error.message);
    } finally {
      // Clear local state and storage
      clearTokens();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      posthog.reset();
    }
  };

  // Refresh token function
  const refreshAccessToken = async () => {
    try {
      const { refreshToken } = getStoredTokens();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Temporarily set the refresh token for this request
      // IMPORTANT: Set skipAuthRefresh flag to prevent infinite loop
      const response = await apiClient.post('/api/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        },
        skipAuthRefresh: true
      });

      const { access_token, refresh_token } = response.data;
      const rememberMe = localStorage.getItem('rememberMe') === 'true';

      // Store new tokens
      storeTokens(access_token, refresh_token, rememberMe);

      // Update axios default header
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, clear tokens and state
      // Don't call logout() here to avoid duplicate API calls
      clearTokens();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      (config) => {
        const { accessToken } = getStoredTokens();
        if (accessToken && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't attempt refresh for:
        // 1. Requests that already tried to refresh (_retry flag)
        // 2. The refresh endpoint itself (skipAuthRefresh flag)
        const shouldSkipRefresh = originalRequest._retry || originalRequest.skipAuthRefresh;

        if (error.response?.status === 401 && !shouldSkipRefresh) {
          originalRequest._retry = true;

          try {
            const newAccessToken = await refreshAccessToken();
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors
    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshAccessToken,
    fetchCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
